import React, { useState } from 'react';
import { styled, width } from '@mui/system';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import DoneIcon from '@mui/icons-material/Done';

// Custom styling for FormControl and Select components
const CustomFormControl = styled(FormControl, {
    shouldForwardProp: (prop) => prop !== 'disabled' && prop !== 'labelColor', // Ensure 'disabled' and 'advancedSearch' are not forwarded to DOM
})(({ theme, disabled, labelColor }) => ({
    width: '100%',
    '& .MuiOutlinedInput-root': {
        minHeight: '56px',
        '& fieldset': {
            borderRadius: '8px',
            borderColor: disabled ? 'lightgray' : 'gray', // Change border color when disabled or advancedSearch
        },
        '&:hover fieldset': {
            borderColor: disabled ? 'lightgray' : 'gray', // Hover border color when disabled or advancedSearch
        },
        '&.Mui-focused fieldset': {
            borderColor: disabled ? 'lightgray' : 'gray', // Focused border color when disabled or advancedSearch
        },
        padding: '8px 10px', // Adjust padding for input box
    },
    '& .MuiInputLabel-root': {
        color: disabled ? 'black' : 'gray', // Set color to black when disabled or advancedSearch        
        whiteSpace: 'break-spaces',
        position: 'absolute',
        left: '16px',
        top: '50%',
        transform: 'translateY(-50%) scale(1)',
        transformOrigin: 'left',
        transition: 'all 0.2s ease-in-out',
        lineHeight: '1.5',
        width: '100%', // Ensure it fits within the input box
        overflowWrap: 'break-word', // Break long words if needed
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: 'gray', // Focused label color
        top: '-10px',
        transform: 'translateY(0) scale(0.75)',
    },
    '& .MuiInputLabel-shrink': {
        top: '-10px',
        transform: 'translateY(0) scale(0.75)',
    },
    '& .MuiInputLabel-root.Mui-disabled': {
        color: labelColor ? labelColor : 'gray', // Set the color to black when disabled
    },
    '& .MuiOutlinedInput-input': {
        padding: '8px 10px', // Adjust input padding for compactness
    },
}));

const MultipleSelect = ({
    label,
    name,
    required,
    options = [],
    mappingKey = 'option',
    formik,
    className,
    maxSelect,
    onValueChange,
    disabled,
    readOnly,
    selectedValues = [],
    noSort = false,
    labelColor
}) => {

    const calculatePaddingTop = (label) => {
        const lineCount = label.split('\n').length;
        let basePadding;

        // Adjust base padding based on label length in smaller increments
        if (label.length >= 800) {
            basePadding = 190;
        } else if (label.length >= 500) {
            basePadding = 130;
        } else if (label.length >= 350) {
            basePadding = 60;
        } else if (label.length >= 200) {
            basePadding = 40;
        } else if (label.length >= 100) {
            basePadding = 20;
        } else {
            basePadding = 10;
        }

        // Additional padding for each line break
        const extraPadding = (lineCount - 1) * 15; // Increase line break padding if needed

        return `${basePadding + extraPadding}px`;
    };

    const paddingTop = calculatePaddingTop(label);


    const values = Array?.isArray(formik?.values?.[name]) ? formik?.values[name] : selectedValues;

    const handleSelectChange = (event) => {
        const {
            target: { value },
        } = event;

        const valueArray = typeof value === 'string' ? value.split(',') : value;
        if (maxSelect) {
            if (value.length <= maxSelect) {
                if (formik) {
                    formik.setFieldValue(name, valueArray);
                } else {
                    onValueChange(name, valueArray);
                }
            }
        } else {
            if (formik) {
                formik.setFieldValue(name, valueArray);
            } else {
                onValueChange(name, valueArray);
            }
        }
    };

    return (
        <CustomFormControl
            disabled={disabled}
            labelColor={labelColor}
            className={className}
            variant="outlined">
            <InputLabel sx={{ color: 'green' }} disabled={disabled} >{label}</InputLabel>
            <Select
                aria-readonly={readOnly}
                label={label}
                required={required}
                name={name}
                multiple
                disabled={disabled}
                value={values}
                onChange={handleSelectChange}
                renderValue={(selected) =>
                    selected.length > 0
                        ? selected
                            .map((item) => (
                                <span
                                    key={item}
                                    style={{
                                        maxWidth: '100px',
                                        display: 'inline-block',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                    title={item}
                                >
                                    {item}
                                </span>
                            ))
                            .reduce((prev, curr) => [prev, ', ', curr])
                        : 'Select options'
                }
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: 200,
                            maxWidth: '100%',
                        },
                    },
                }}
                style={{ borderRadius: '8px', borderColor: 'gray', paddingTop: paddingTop }}
            >
                {options && options.length > 0 ? (
                    options.map((option, index) => (
                        <MenuItem
                            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} // Align text and icon
                            key={index + 1}
                            value={
                                name === 'DRQ-00077'
                                    ? option[mappingKey] || `${option?.option}-${option?.value}`
                                    : option[mappingKey] || option?.option
                            }
                        >
                            <span>{option[mappingKey] || option?.option}</span>
                            {values.includes(
                                name === 'DRQ-00077'
                                    ? option[mappingKey] || `${option?.option}-${option?.value}`
                                    : option[mappingKey] || option?.option
                            ) && <DoneIcon className="text-[#0000ff99]" />}
                        </MenuItem>
                    ))
                ) : (
                    <MenuItem value="">No options available</MenuItem>
                )}
            </Select>
        </CustomFormControl>
    );
};

export default MultipleSelect;